import React, { useState, useRef } from "react";
import { Copy, Check } from "lucide-react";
import { Container, Section, Eyebrow, Display, Hairline, SEO } from "./ui";
import { canonicalFor } from "../routes";
import { logValentCopy } from "../utils/api";

// The signature image must point at a publicly-hosted absolute URL so it loads
// inside email clients. The on-page preview uses a relative path so it also
// renders in local dev (served from /public).
const LOGO_ABSOLUTE = "https://whobrew.us/valent-logo.png";
const LOGO_PREVIEW = "/valent-logo.png";

const LINK = "#2563eb";
// Signature renders in Inter (matching the install copy), with an email-safe
// fallback stack for clients that don't have Inter installed.
const SIG_FONT =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";

// Optional fields users can toggle into the signature. No "View my card" /
// photo / link options from the logged-in generator — those need an account,
// which /valent visitors never have. These are the ones that make sense here.
type OptKey = "title" | "mobile" | "website" | "linkedin";
const OPT_FIELDS: { key: OptKey; label: string; placeholder: string; type: string }[] = [
  { key: "title", label: "Title", placeholder: "Managing Partner", type: "text" },
  { key: "mobile", label: "Mobile", placeholder: "555-555-1234", type: "tel" },
  { key: "website", label: "Website", placeholder: "valentpartners.com", type: "text" },
  { key: "linkedin", label: "LinkedIn", placeholder: "linkedin.com/in/you", type: "text" },
];

type OptState = Record<OptKey, { on: boolean; value: string }>;

const hrefOf = (raw: string) =>
  /^https?:\/\//.test(raw) ? raw : `https://${raw}`;
const labelOf = (raw: string) => raw.replace(/^https?:\/\//, "");

const ValentSignature: React.FC = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [extras, setExtras] = useState<OptState>({
    title: { on: false, value: "" },
    mobile: { on: false, value: "" },
    website: { on: false, value: "" },
    linkedin: { on: false, value: "" },
  });
  const [copied, setCopied] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const active = (k: OptKey) => extras[k].on && extras[k].value.trim() !== "";

  const generateHtml = (logoUrl: string): string => {
    const displayName = name.trim() || "Your Name";
    const title = active("title") ? extras.title.value.trim() : "";

    // Contact line: phone | mobile | email (only the ones present).
    const contacts: string[] = [];
    if (phone.trim()) contacts.push(`<span style="color: #222222;">${phone.trim()}</span>`);
    if (active("mobile")) contacts.push(`<span style="color: #222222;">${extras.mobile.value.trim()}</span>`);
    if (email.trim()) {
      contacts.push(
        `<a href="mailto:${email.trim()}" style="color: ${LINK}; text-decoration: underline;">${email.trim()}</a>`
      );
    }
    // &nbsp; before the pipe keeps it glued to the prior item; the trailing
    // space is a wrap opportunity so long values drop to the next line instead
    // of overflowing on narrow screens.
    const contactLine = contacts.join(`&nbsp;<span style="color: #888888;">|</span> `);

    // Links line: website · linkedin (only the ones present).
    const links: string[] = [];
    if (active("website")) {
      const w = extras.website.value.trim();
      links.push(`<a href="${hrefOf(w)}" style="color: ${LINK}; text-decoration: none;">${labelOf(w)}</a>`);
    }
    if (active("linkedin")) {
      const l = extras.linkedin.value.trim();
      links.push(`<a href="${hrefOf(l)}" style="color: ${LINK}; text-decoration: none;">${labelOf(l)}</a>`);
    }
    const linksLine = links.join(`&nbsp;<span style="color: #888888;">·</span> `);

    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family: ${SIG_FONT}; font-size: 14px; line-height: 1.4;">
  <tr><td style="padding: 0 0 2px;">
    <span style="font-weight: 700; font-size: 15px; color: #1a1a1a;">${displayName}</span>
  </td></tr>
  ${title ? `<tr><td style="padding: 0 0 4px; font-size: 13px; color: #555555;">${title}</td></tr>` : ""}
  ${contactLine ? `<tr><td style="padding: 0 0 ${linksLine ? "4px" : "12px"}; font-size: 13px;">${contactLine}</td></tr>` : ""}
  ${linksLine ? `<tr><td style="padding: 0 0 12px; font-size: 13px;">${linksLine}</td></tr>` : ""}
  <tr><td>
    <img src="${logoUrl}" alt="Valent Partners" width="180" style="display: block; border: 0; width: 180px; height: auto;" />
  </td></tr>
</table>`;
  };

  const handleCopy = async () => {
    // Always copy the absolute-URL variant so the logo loads in the email.
    const html = generateHtml(LOGO_ABSOLUTE);
    try {
      const blob = new Blob([html], { type: "text/html" });
      await navigator.clipboard.write([new ClipboardItem({ "text/html": blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        await navigator.clipboard.writeText(html);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    }
    // Log every optional field the user filled in (regardless of whether its
    // toggle is currently on) so the admin sees all values. Fire-and-forget.
    const optionals: Record<string, string> = {};
    (["title", "mobile", "website", "linkedin"] as const).forEach((k) => {
      const v = extras[k].value.trim();
      if (v) optionals[k] = v;
    });
    logValentCopy({
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      fields: optionals,
    }).catch(() => {});
  };

  const toggle = (k: OptKey) =>
    setExtras((s) => ({ ...s, [k]: { ...s[k], on: !s[k].on } }));
  const setVal = (k: OptKey, value: string) =>
    setExtras((s) => ({ ...s, [k]: { ...s[k], value } }));

  return (
    <>
      <SEO
        title="Email signature — Valent Partners"
        description="Generate your Valent Partners email signature."
        canonical={canonicalFor("/valent")}
        noindex
      />

      <Section tone="page" spacing="default">
        <Container width="default">
          <Eyebrow>Valent Partners</Eyebrow>
          <Display as="h1" scale="sm" italicWord="mark" className="mt-2 mb-10">
            Make your mark.
          </Display>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Form */}
            <div className="space-y-6 min-w-0">
              <Field label="Name" id="valent-name">
                <input
                  id="valent-name" type="text" value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="field-input"
                  placeholder="Sean Thomas"
                />
              </Field>
              <Field label="Phone" id="valent-phone">
                <input
                  id="valent-phone" type="tel" value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="field-input"
                  placeholder="555-555-5555"
                />
              </Field>
              <Field label="Email" id="valent-email">
                <input
                  id="valent-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field-input"
                  placeholder="first.last@valentpartners.com"
                />
              </Field>

              <style>{`
                .field-input {
                  width: 100%;
                  padding: 0.75rem 1rem;
                  background: transparent;
                  border: 1px solid #c9b89e;
                  border-radius: 4px;
                  color: inherit;
                  font-family: inherit;
                  font-size: 1rem;
                  outline: none;
                  transition: border-color 150ms;
                }
                .field-input:focus { border-color: #1f1410; }
                .dark .field-input { border-color: #443a30; }
                .dark .field-input:focus { border-color: #fbf7f1; }
              `}</style>
            </div>

            {/* Preview — code-style snippet */}
            <div className="lg:sticky lg:top-24 min-w-0 w-full">
              <div className="rounded-lg border border-latte-300 dark:border-latte-800 bg-white overflow-hidden shadow-sm">
                {/* Filename tab + copy control */}
                <div className="flex items-center justify-between px-4 h-10 bg-cream-100 dark:bg-coffee-800 border-b border-latte-300 dark:border-latte-800">
                  <span className="font-mono text-eyebrow uppercase text-coffee-500 dark:text-cream-300">
                    signature
                  </span>
                  <button
                    type="button"
                    onClick={handleCopy}
                    aria-label="Copy signature to clipboard"
                    className="inline-flex items-center gap-1.5 rounded-md border border-latte-300 dark:border-latte-700 px-2 py-1 font-mono text-[11px] tracking-wide text-coffee-600 dark:text-cream-200 hover:bg-cream-50 dark:hover:bg-coffee-700 transition-colors"
                  >
                    {copied ? (<><Check className="w-3 h-3" />Copied</>) : (<><Copy className="w-3 h-3" />Copy</>)}
                  </button>
                </div>
                <div className="p-4 sm:p-6 overflow-x-auto">
                  <div ref={previewRef} dangerouslySetInnerHTML={{ __html: generateHtml(LOGO_PREVIEW) }} />
                </div>
              </div>
              <p className="mt-2 px-1 text-sm text-coffee-500 dark:text-cream-300">
                Copies formatted HTML — paste it straight into your email settings.
              </p>
            </div>
          </div>

          <Hairline className="my-12" />

          {/* Optional details — toggle anything else into the signature */}
          <div>
            <Eyebrow>Add details (optional)</Eyebrow>
            <p className="mt-2 mb-5 text-sm text-coffee-600 dark:text-cream-300">
              Toggle on anything else you'd like in your signature.
            </p>
            {/* auto-fit grid: cards size to fit and wrap to the next row on
                narrow screens instead of overflowing. */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
              {OPT_FIELDS.map((f) => {
                const st = extras[f.key];
                return (
                  <div
                    key={f.key}
                    className="min-w-0 rounded-lg border border-latte-300 dark:border-latte-800 p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-eyebrow uppercase text-coffee-700 dark:text-cream-200">
                        {f.label}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={st.on}
                        aria-label={`Include ${f.label}`}
                        onClick={() => toggle(f.key)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                          st.on ? "bg-coffee-600" : "bg-latte-300 dark:bg-coffee-700"
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                            st.on ? "translate-x-4" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                    </div>
                    {st.on && (
                      <input
                        type={f.type}
                        value={st.value}
                        onChange={(e) => setVal(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        className="field-input mt-3"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Hairline className="my-12" />

          {/* Install help */}
          <div className="space-y-3 text-body text-coffee-700 dark:text-cream-200">
            <Eyebrow>How to install</Eyebrow>
            <p className="pt-2">
              <span className="font-semibold text-coffee-900 dark:text-cream-50">Gmail</span> — Settings
              → See all settings → General → Signature → paste.
            </p>
            <p>
              <span className="font-semibold text-coffee-900 dark:text-cream-50">Outlook</span> — Settings
              → Mail → Compose and reply → Email signature → paste.
            </p>
            <p>
              <span className="font-semibold text-coffee-900 dark:text-cream-50">Apple Mail</span> — Mail
              → Settings → Signatures → paste.
            </p>
          </div>
        </Container>
      </Section>
    </>
  );
};

const Field: React.FC<{ label: string; id: string; children: React.ReactNode }> = ({
  label,
  id,
  children,
}) => (
  <div>
    <label
      htmlFor={id}
      className="block font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-2"
    >
      {label}
    </label>
    {children}
  </div>
);

export default ValentSignature;
